// src/pages/Admin/PagesPersonnalisees/utils/pagesPersonnaliseesUtils.js

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const INITIAL_PAGE_FORM = {
  slug: "",
  titre: "",
  introduction: "",
  actif: true,
  afficher_menu: true,
  ordre_menu: 0,
  meta_title: "",
  meta_description: "",
};

export const INITIAL_ARTICLE_FORM = {
  page_id: "",
  titre_article: "",
  contenu_texte: "",
  url_image: "",
  image_alt: "",
  ordre: 0,
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const generateSlug = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 255);

export const truncateText = (value = "", limit = 140) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();

  if (text.length <= limit) return text;

  return `${text.slice(0, limit).trim()}...`;
};

export const getPageDate = (page) =>
  page?.created_at || page?.createdAt || page?.updated_at || page?.updatedAt;

export const getPageArticles = (page) => {
  if (Array.isArray(page?.articles)) return page.articles;
  if (Array.isArray(page?.Articles)) return page.Articles;
  return [];
};

export const getFirstArticleImage = (page) => {
  const articles = getPageArticles(page);
  return articles.find((article) => article?.url_image)?.url_image || "";
};

export const getStatusLabel = (isActive) => (isActive ? "Active" : "Inactive");

export const getMenuLabel = (showMenu) =>
  showMenu ? "Dans le menu" : "Masquée du menu";

export const getStatusClass = (isActive) =>
  isActive
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

export const getMenuClass = (showMenu) =>
  showMenu
    ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20"
    : "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";

export const filterAndSortPages = (pages = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const status = filters.status || "all";
  const menu = filters.menu || "all";

  let result = [...pages];

  if (status === "active") {
    result = result.filter((page) => Boolean(page.actif));
  }

  if (status === "inactive") {
    result = result.filter((page) => !Boolean(page.actif));
  }

  if (menu === "visible") {
    result = result.filter((page) => Boolean(page.afficher_menu));
  }

  if (menu === "hidden") {
    result = result.filter((page) => !Boolean(page.afficher_menu));
  }

  if (query) {
    result = result.filter((page) => {
      const haystack = normalizeText(
        [
          page.titre,
          page.slug,
          page.introduction,
          page.meta_title,
          page.meta_description,
          getPageArticles(page)
            .map((article) =>
              [
                article.titre_article,
                article.contenu_texte,
                article.image_alt,
              ].join(" ")
            )
            .join(" "),
        ].join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "menu_asc") {
      return Number(a.ordre_menu || 0) - Number(b.ordre_menu || 0);
    }

    if (filters.sortOrder === "menu_desc") {
      return Number(b.ordre_menu || 0) - Number(a.ordre_menu || 0);
    }

    if (filters.sortOrder === "created_asc") {
      return new Date(getPageDate(a) || 0) - new Date(getPageDate(b) || 0);
    }

    if (filters.sortOrder === "title_asc") {
      return normalizeText(a.titre).localeCompare(normalizeText(b.titre));
    }

    if (filters.sortOrder === "title_desc") {
      return normalizeText(b.titre).localeCompare(normalizeText(a.titre));
    }

    if (filters.sortOrder === "articles_desc") {
      return getPageArticles(b).length - getPageArticles(a).length;
    }

    return new Date(getPageDate(b) || 0) - new Date(getPageDate(a) || 0);
  });

  return result;
};

export const buildPagesStats = (pages = [], filteredPages = []) => {
  const active = pages.filter((page) => Boolean(page.actif)).length;
  const inactive = pages.length - active;

  const menuVisible = pages.filter((page) => Boolean(page.afficher_menu)).length;
  const menuHidden = pages.length - menuVisible;

  const articles = pages.reduce(
    (total, page) => total + getPageArticles(page).length,
    0
  );

  const emptyPages = pages.filter(
    (page) => getPageArticles(page).length === 0
  ).length;

  return {
    total: pages.length,
    filtered: filteredPages.length,
    active,
    inactive,
    menuVisible,
    menuHidden,
    articles,
    emptyPages,
  };
};

export const normalizePagePayload = (formData) => {
  const titre = String(formData.titre || "").trim();
  const slug = generateSlug(formData.slug || titre);

  return {
    ...formData,
    slug,
    titre,
    introduction: String(formData.introduction || "").trim(),
    actif: Boolean(formData.actif),
    afficher_menu: Boolean(formData.afficher_menu),
    ordre_menu: Number(formData.ordre_menu || 0),
    meta_title: String(formData.meta_title || "").trim(),
    meta_description: String(formData.meta_description || "").trim(),
  };
};

export const validatePageForm = (formData) => {
  if (!String(formData.titre || "").trim()) {
    return "Le titre de la page est obligatoire.";
  }

  if (!String(formData.slug || "").trim()) {
    return "Le slug de la page est obligatoire.";
  }

  return null;
};

export const buildInitialFormFromPage = (page) => {
  if (!page) return INITIAL_PAGE_FORM;

  return {
    ...INITIAL_PAGE_FORM,
    ...page,
    actif: Boolean(page.actif),
    afficher_menu: Boolean(page.afficher_menu),
    ordre_menu: Number(page.ordre_menu || 0),
  };
};

export const normalizeArticlePayload = (formData) => ({
  ...formData,
  page_id: Number(formData.page_id),
  titre_article: String(formData.titre_article || "").trim(),
  contenu_texte: String(formData.contenu_texte || "").trim(),
  url_image: String(formData.url_image || "").trim(),
  image_alt: String(formData.image_alt || "").trim(),
  ordre: Number(formData.ordre || 0),
});

export const validateArticleForm = (formData) => {
  if (!formData.page_id) {
    return "La page associée est obligatoire.";
  }

  if (
    !String(formData.titre_article || "").trim() &&
    !String(formData.contenu_texte || "").trim() &&
    !String(formData.url_image || "").trim()
  ) {
    return "Ajoutez au moins un titre, un texte ou une image.";
  }

  return null;
};

export const buildInitialArticleForm = (pageId, article = null) => {
  if (!article) {
    return {
      ...INITIAL_ARTICLE_FORM,
      page_id: pageId,
      ordre: 0,
    };
  }

  return {
    ...INITIAL_ARTICLE_FORM,
    ...article,
    page_id: article.page_id || pageId,
    ordre: Number(article.ordre || 0),
  };
};